import { createRouter, createWebHistory } from 'vue-router'
import { setAllenContext } from '@/composables/allenContext'

import Home from '../components/Home.vue'
import HomeOverview from '../components/HomeOverview.vue'
import CameraOverview from '../components/CameraOverview.vue'
import RealTimeDashboard from '../components/RealTimeDashboard.vue'
import FlowSettings from '../components/FlowSettings.vue'
import DetectionZoneData from '../components/DetectionZoneData.vue'
import NewFlow from '../components/NewFlow.vue'
import FlowOutput from '../components/FlowOutput.vue'
import APIOverview from '../components/APIOverview.vue'
import NewAPIConnection from '../components/NewAPIConnection.vue'
import Settings from '../components/Settings.vue'
import Registration from '../components/Registration.vue'
import Login from '../components/Login.vue'
import ChangePassword from '../components/ChangePassword.vue'
import ForgotPassword from '../components/ForgotPassword.vue'
import ResetPassword from '../components/ResetPassword.vue'
import SelectCompany from '../components/SelectCompany.vue'
import StoralyticDemoStore from '../components/StoralyticDemoStore.vue'
import StoralyticDemoHomePage from '../components/StoralyticDemoHomePage.vue'
import StoralyticDemoDetectionZoneData from '../components/StoralyticDemoDetectionZoneData.vue'
import PromotionsView from '../components/PromotionsView.vue'
import PromoVerdictView from '../components/PromoVerdictView.vue'
import StoralyticDemoPromos from '../components/StoralyticDemoPromos.vue'
import StoralyticDemoPromoDetail from '../components/StoralyticDemoPromoDetail.vue'
import SectorCalendarsPage from '../components/SectorCalendarsPage.vue'

import { useUserStore } from '@/stores/UserStore'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),

  routes: [
    {
      path: '/',
      name: 'Home',
      component: HomeOverview,
      meta: { requiresAuth: true }
    },
    {
      path: '/store_level',
      name: 'StoreLevel',
      component: Home,
      meta: { requiresAuth: true }
    },
    // Promotions — its own destination, not a fourth tab on Store Level. The three
    // tabs there are a drill-down narrative sharing one period picker; a promo is not
    // a step in it and carries its own pinned windows. The demo store mirrors these
    // two routes under /storalytic_demo_store/. See docs/reference/promo_measurement.md.
    {
      path: '/promotions',
      name: 'Promotions',
      component: PromotionsView,
      meta: { requiresAuth: true }
    },
    {
      path: '/promotions/:promoId',
      name: 'PromoVerdict',
      component: PromoVerdictView,
      meta: { requiresAuth: true },
      props: true
    },
    {
        path: '/zone/:DetectionZoneId',
        name: 'DetectionZoneData',
        component: DetectionZoneData,
        meta: { requiresAuth: true }
    },
    {
      path: '/real_time_dashboard',
      name: 'RealTimeDashboard',
      component: RealTimeDashboard,
      meta: { requiresAuth: true }
    },
    {
      path: '/camera_overview',
      name: 'CameraOverview',
      component: CameraOverview,
      meta: { requiresAuth: true }
    },
    {
      path: '/flow/:id',
      name: 'FlowOutput',
      component: FlowOutput,
      meta: { requiresAuth: true }
    },
    {
      path: '/flow_settings/:id',
      name: 'FlowSettings',
      component: FlowSettings,
      meta: { requiresAuth: true }
    },
    {
      path: '/create_flow',
      name: 'NewFlow',
      component: NewFlow,
      meta: { requiresAuth: true }
    },
    {
      path: '/external_data',
      name: 'APIOverview',
      component: APIOverview,
      meta: { requiresAuth: true }
    },
    {
      path: '/connect_api',
      name: 'NewAPIConnection',
      component: NewAPIConnection,
      meta: { requiresAuth: true }
    },
    {
      path: '/settings',
      name: 'Settings',
      component: Settings,
      meta: { requiresAuth: true }
    },
    {
      path: '/registration',
      name: 'Registration',
      component: Registration,
      meta: { requiresAuth: true }
    },
    // Platform-wide, not one store's: reached from the company-switch page (super-admins).
    {
      path: '/sector_calendars',
      name: 'SectorCalendars',
      component: SectorCalendarsPage,
      meta: { requiresAuth: true, superAdminOnly: true }
    },
    {
      path: '/login',
      name: 'Login',
      component: Login,
      meta: { isPublic: true }
    },
    {
      path: '/change-password',
      name: 'ChangePassword',
      component: ChangePassword,
      meta: { requiresAuth: true }
    },
    {
      path: '/forgot-password',
      name: 'ForgotPassword',
      component: ForgotPassword
    },
    {
      path: '/reset-password',
      name: 'ResetPassword',
      component: ResetPassword
    },
    {
      path: '/select-company',
      name: 'select-company',
      component: SelectCompany,
      meta: { isPublic: true }
    },
    {
      path: '/storalytic_demo_store',
      name: 'Storalytic Demo Home',
      component: StoralyticDemoHomePage,
      meta: { isPublic: true, isDemo: true }
    },
    {
      path: '/storalytic_demo_store/store_level',
      name: 'Storalytic Demo Store',
      component: StoralyticDemoStore,
      meta: { isPublic: true, isDemo: true }
    },
    {
      path: '/storalytic_demo_store/zone/:zoneName',
      name: 'StoralyticDemoDetectionZoneData',
      component: StoralyticDemoDetectionZoneData,
      meta: { isPublic: true, isDemo: true },
      props: true
    },
    // Promotions — its own destination, not a fourth tab on Store Level. The three
    // tabs there are a drill-down narrative (what to do -> why -> raw numbers)
    // sharing one period picker; a promo is not a step in it and carries its own
    // per-promo windows. See docs/reference/promo_measurement.md §15.
    {
      path: '/storalytic_demo_store/promotions',
      name: 'StoralyticDemoPromos',
      component: StoralyticDemoPromos,
      meta: { isPublic: true, isDemo: true }
    },
    {
      path: '/storalytic_demo_store/promotions/:promoId',
      name: 'StoralyticDemoPromoDetail',
      component: StoralyticDemoPromoDetail,
      meta: { isPublic: true, isDemo: true },
      props: true
    },
  ],

  //Always scroll to top
  scrollBehavior(to, from, savedPosition) {
    
    return { top: 0 }
  },
})

// function isAuthenticated() {
//   // Check if the token is stored in local storage
//   return !!localStorage.getItem('authToken') // Replace 'authToken' with the actual key you use
// }

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore()
  const hasToken = !!localStorage.getItem('authToken')
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Populate store lazily if needed
  if (hasToken && !userStore.user.UserName) {
    await userStore.fetchUserDetails()
  }

  // 1) public routes
  if (to.meta.isPublic) {
    if (to.name === 'select-company') {
      if (!hasToken || (!userStore.isSuperAdmin && !userStore.isSuperUser)) {
        return next('/') 
      }
    }
    return next()
  }

  // 2) standard protected route: must have a token
  if (requiresAuth && !hasToken) {
    return next('/login')
  }

  // 3a) super-admin-only pages (sector calendars)
  if (to.meta.superAdminOnly && !userStore.isSuperAdmin) {
    return next('/')
  }

  // 3) extra lock for Registration: only Super Admin via SelectCompany
  if (to.name === 'Registration') {
    if (!userStore.isSuperAdmin) {
      return next('/') // not a Super Admin
    }
    const pass = sessionStorage.getItem('allowRegistrationOnce') === '1'
    if (!pass) {
      return next('/') // didn’t come from SelectCompany button
    }
    // consume the one-time pass
    sessionStorage.removeItem('allowRegistrationOnce')
    return next()
  }
  
  return next() // Proceed if the route is public or user is authenticated
})

// Coarse per-route base context for the docked Ask Allen. The dashboard
// (StoreLevel) and per-zone views refine it further (tab + zone name); anything
// unmapped falls back to general.
router.afterEach((to) => {
  const byName = {
    Home: 'today',
    'Storalytic Demo Home': 'today',
    StoreLevel: 'explore',                 // FixedWidgets refines to explore/zones/actions
    'Storalytic Demo Store': 'explore',
    DetectionZoneData: 'zone',             // the view sets the zone name
    StoralyticDemoDetectionZoneData: 'zone',
    FlowOutput: 'zone',
  }
  setAllenContext(byName[to.name] || 'general')
})

export default router